import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEvent, useDeleteEvent, useUpdateEventStatus } from '@/lib/api/hooks/events';
import { ActionDropdown, Button, DeleteModal, PublishModal, UnpublishModal, type DropdownAction } from '@/components/ui';
import { showToast } from '@/lib/toast';
import { CalendarDays, MapPin, FileText, User, PenLine, CalendarRange, Image, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { sanitizeHtml } from '@/utils/helpers/html';

const EventDetailPage: React.FC = () => {
    const { id: eventId } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const [selectedAdditionalImageIndex, setSelectedAdditionalImageIndex] = useState<number | null>(null);

    interface EventDetail {
        eventId: string;
        imageUrl: string;
        eventTitle: string;
        eventVenue: string;
        startDate: string | null;
        endDate: string | null;
        description: string;
        additionalImageUrls?: string[];
        additionalFileUrls?: string[];
        status: 'PUBLISH' | 'DRAFT';
        datePublished: string | null;
        publishedByName: string | null;
        createdByName: string | null;
    }

    const { data: eventData, isLoading, error } = useEvent(eventId!);
    const event = eventData?.data as unknown as EventDetail;
    const additionalImageUrls = event?.additionalImageUrls || [];
    const additionalFileUrls = event?.additionalFileUrls || [];

    const { mutate: deleteEvent, isPending: isDeleting } = useDeleteEvent();
    const { mutate: updateStatus, isPending: isUpdatingStatus } = useUpdateEventStatus();

    if (!eventId) {
        return (
            <div className="text-center p-8 bg-white rounded-lg">
                <h2 className="text-xl font-bold">Event ID missing</h2>
                <button onClick={() => navigate('/admin/events')} className="mt-4 text-[#681628] hover:underline">
                    &larr; Back to Events
                </button>
            </div>
        );
    }

    const formatDateTime = (dateString: string | null | undefined) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
            }) + ' at ' + date.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
            });
        } catch {
            return dateString;
        }
    };

    const handleEdit = () => {
        navigate(`/admin/events/edit/${eventId}`);
    };

    const handleDeleteClick = () => {
        setDeleteModalOpen(true);
    };

    const handleStatusUpdateClick = () => {
        if (!event) return;
        setStatusModalOpen(true);
    };

    const openAdditionalImage = (index: number) => setSelectedAdditionalImageIndex(index);
    const closeAdditionalImage = () => setSelectedAdditionalImageIndex(null);
    const showPrevAdditionalImage = () => {
        if (!additionalImageUrls.length || selectedAdditionalImageIndex === null) return;
        const nextIndex = (selectedAdditionalImageIndex - 1 + additionalImageUrls.length) % additionalImageUrls.length;
        setSelectedAdditionalImageIndex(nextIndex);
    };
    const showNextAdditionalImage = () => {
        if (!additionalImageUrls.length || selectedAdditionalImageIndex === null) return;
        const nextIndex = (selectedAdditionalImageIndex + 1) % additionalImageUrls.length;
        setSelectedAdditionalImageIndex(nextIndex);
    };

    const getFileNameFromUrl = (url: string): string => {
        const cleaned = url.split('?')[0].split('#')[0];
        const name = cleaned.split('/').pop();
        return name && name.trim() ? decodeURIComponent(name) : 'document';
    };

    const confirmDelete = () => {
        deleteEvent(eventId, {
            onSuccess: (response) => {
                if (response.success) {
                    showToast.success('Event deleted successfully!');
                    setDeleteModalOpen(false);
                    navigate('/admin/events');
                }
            },
            onError: (error: Error) => {
                showToast.error('Failed to delete: ' + (error.message || 'Unknown error'));
            }
        });
    };

    const confirmStatusUpdate = () => {
        if (!event) return;
        const newStatus = event.status === 'PUBLISH' ? 'DRAFT' : 'PUBLISH';

        updateStatus(
            { eventId, status: newStatus },
            {
                onSuccess: (response) => {
                    if (response.success) {
                        showToast.success(
                            newStatus === 'PUBLISH'
                                ? 'Event published successfully!'
                                : 'Event unpublished successfully!'
                        );
                        setStatusModalOpen(false);
                    }
                },
                onError: (error: Error) => {
                    showToast.error('Failed to update status: ' + (error.message || 'Unknown error'));
                }
            }
        );
    };

    const getDropdownActions = (): DropdownAction[] => [
        {
            label: 'Edit',
            onClick: (e) => {
                e.stopPropagation();
                handleEdit();
            }
        },
        {
            label: event?.status === 'PUBLISH' ? 'Unpublish' : 'Publish',
            onClick: (e) => {
                e.stopPropagation();
                handleStatusUpdateClick();
            },
            disabled: isUpdatingStatus
        },
        {
            label: 'Delete',
            onClick: (e) => {
                e.stopPropagation();
                handleDeleteClick();
            },
            variant: 'danger' as const,
            disabled: isDeleting
        }
    ];

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#681628] mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading event...</p>
                </div>
            </div>
        );
    }

    if (error || !event) {
        return (
            <div className="text-center p-8 bg-white rounded-lg">
                <h2 className="text-xl font-bold text-red-600">Error loading event</h2>
                <p className="mt-2 text-gray-600">{error?.message || 'Event not found'}</p>
                <button onClick={() => navigate('/admin/events')} className="mt-4 text-[#681628] hover:underline">
                    &larr; Back to Events
                </button>
            </div>
        );
    }

    return (
        <>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-4">
                        {/* Circular icon */}
                        <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-[#F8E8EB]">
                            <CalendarDays className="h-8 w-8 text-[#8F2F44]" />
                        </div>
                        <div>
                            <div className="flex flex-wrap items-center gap-4">
                                <h1 className="text-2xl font-bold text-gray-900">{event.eventTitle}</h1>
                                <span className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1 text-sm font-semibold ${
                                    event.status === 'PUBLISH'
                                        ? 'border-[#CFEED7] bg-[#E8F6EE] text-[#0F8A47]'
                                        : 'border-gray-200 bg-gray-100 text-gray-700'
                                }`}>
                                    <span className={`h-2 w-2 rounded-full ${event.status === 'PUBLISH' ? 'bg-[#0F8A47]' : 'bg-gray-500'}`} />
                                    {event.status === 'PUBLISH' ? 'Published' : 'Draft'}
                                </span>
                            </div>
                            <p className="mt-1 text-sm text-gray-500">Event details and information</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                            <Button
                            type="button"
                            onClick={handleEdit}
                            className="inline-flex items-center gap-2 bg-[#0F8A47] px-3 text-sm font-semibold text-white shadow-sm hover:bg-[#0C7A3D] transition-colors"
                        >
                            <PenLine className="h-4 w-4" />
                            Edit Event
                        </Button>
                        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
                            <ActionDropdown
                                id={`event-${eventId}`}
                                actions={getDropdownActions()}
                            />
                        </div>
                    
                    </div>
                </div>

                {/* Card 1: Venue + Date & Time */}
                <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
                    <div className="grid grid-cols-1 md:grid-cols-2 p-6 gap-6">
                        {/* Venue */}
                        <div className="p-6 border rounded border-gray-200 ">
                            <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                                <MapPin className="h-4 w-4 text-[#8F2F44]" />
                                <span className="font-medium">Venue</span>
                            </div>
                            <p className="text-base font-semibold text-gray-900">{event.eventVenue || 'N/A'}</p>
                        </div>

                        {/* Date & Time */}
                           <div className="p-6 border rounded border-gray-200 ">
                            <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                                <CalendarRange className="h-4 w-4 text-[#8F2F44]" />
                                <span className="font-medium">Date &amp; Time</span>
                            </div>
                            <div className="flex flex-wrap items-center gap-3">
                                <p className="text-base font-semibold text-gray-900">
                                    {event.startDate ? formatDateTime(event.startDate) : 'N/A'}
                                </p>
                                {(event.startDate || event.endDate) && (
                                    <span className="text-gray-400">→</span>
                                )}
                                <p className="text-base font-semibold text-gray-900">
                                    {event.endDate ? formatDateTime(event.endDate) : 'N/A'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Card 2: Event Image + Description */}
                <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
                    <div className="grid grid-cols-1  md:grid-cols-2 gap-6">
                        {/* Event Image */}
                        <div className="p-6">
                            <div className="flex items-center gap-2 text-base text-gray-500 mb-4">
                                <Image className="h-6 w-6 text-[#8F2F44]" />
                                <span className="font-medium">Event Image</span>
                            </div>
                            {event.imageUrl ? (
                                <img
                                    src={event.imageUrl}
                                    alt={event.eventTitle}
                                    className="w-full rounded-xl object-cover max-h-80"
                                />
                            ) : (
                                <div className="flex h-48 w-full items-center justify-center rounded-xl bg-gray-100 text-gray-400">
                                    No image
                                </div>
                            )}
                        </div>

                        {/* Description */}
                        <div className="p-6">
                            <div className="flex items-center gap-2 text-base text-gray-500 mb-4">
                                <FileText className="h-6 w-6 text-[#8F2F44]" />
                                <span className="font-medium">Description</span>
                            </div>
                           {event.description ? <div className="min-h-[120px] rounded-xl border border-gray-200 bg-[#FAFAFA] p-4 text-base text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: sanitizeHtml(event.description) }} /> : <span className="text-gray-400 italic">No description provided.</span>}
                        </div>
                    </div>
                </div>

                {(additionalImageUrls.length > 0 || additionalFileUrls.length > 0) && (
                    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6 space-y-5">
                        <p className="text-sm font-semibold text-gray-500">Additional Assets</p>

                        {additionalImageUrls.length > 0 && (
                            <div className="space-y-3">
                                <p className="text-sm font-medium text-gray-700">Additional Images</p>
                                <div className="flex gap-3 overflow-x-auto pb-2">
                                    {additionalImageUrls.map((url, index) => (
                                        <button
                                            key={`${url}-${index}`}
                                            type="button"
                                            onClick={() => openAdditionalImage(index)}
                                            className="shrink-0 rounded-lg border border-gray-200 overflow-hidden hover:border-[#8F2F44] transition-colors"
                                            aria-label={`Open additional image ${index + 1}`}
                                        >
                                            <img src={url} alt={`Additional image ${index + 1}`} className="w-36 h-24 object-cover" />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {additionalFileUrls.length > 0 && (
                            <div className="space-y-3">
                                <p className="text-sm font-medium text-gray-700">Additional Files</p>
                                <ul className="space-y-2">
                                    {additionalFileUrls.map((url, index) => (
                                        <li key={`${url}-${index}`}>
                                            <a
                                                href={url}
                                                target="_blank"
                                                rel="noreferrer noopener"
                                                className="text-sm text-[#8F2F44] hover:underline break-all"
                                            >
                                                {getFileNameFromUrl(url)}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}

                {/* Card 3: Publishing Information */}
                <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6">
                    <p className="text-sm font-semibold text-gray-500 mb-6">Publishing Information</p>
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-gray-200">
                        <div className="flex items-start gap-3 pb-4 sm:pb-0">
                            <CalendarDays className="h-5 w-5 shrink-0 text-[#8F2F44] mt-0.5" />
                            <div>
                                <p className="text-sm text-gray-500">Date Published</p>
                                <p className="mt-1 text-base font-semibold text-gray-900">
                                    {event.datePublished ? formatDateTime(event.datePublished) : 'Not published'}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3 pt-4 sm:pt-0 sm:pl-6">
                            <User className="h-5 w-5 shrink-0 text-[#8F2F44] mt-0.5" />
                            <div>
                                <p className="text-sm text-gray-500">Published By</p>
                                <p className="mt-1 text-base font-semibold text-gray-900">{event.publishedByName || 'N/A'}</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3 pt-4 sm:pt-0 sm:pl-6">
                            <PenLine className="h-5 w-5 shrink-0 text-[#8F2F44] mt-0.5" />
                            <div>
                                <p className="text-sm text-gray-500">Created By</p>
                                <p className="mt-1 text-base font-semibold text-gray-900">{event.createdByName || 'N/A'}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Delete Modal */}
            <DeleteModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={confirmDelete}
                title="Delete Event"
                subtitle="Are you sure you want to delete this event?"
                warningText="This action cannot be undone."
                isLoading={isDeleting}
                itemType="Event"
            />

            {/* Publish/Unpublish Modals */}
            {event.status === 'DRAFT' ? (
                <PublishModal
                    isOpen={statusModalOpen}
                    onClose={() => setStatusModalOpen(false)}
                    onConfirm={confirmStatusUpdate}
                    title="Publish Event"
                    subtitle="Are you sure you want to publish this event?"
                    itemType="Event"
                    isLoading={isUpdatingStatus}
                />
            ) : (
                <UnpublishModal
                    isOpen={statusModalOpen}
                    onClose={() => setStatusModalOpen(false)}
                    onConfirm={confirmStatusUpdate}
                    itemType="Event"
                    isLoading={isUpdatingStatus}
                />
            )}

            {selectedAdditionalImageIndex !== null && additionalImageUrls.length > 0 && (
                <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
                    <button
                        type="button"
                        onClick={closeAdditionalImage}
                        className="absolute top-4 right-4 p-2 rounded-full bg-white/20 text-white hover:bg-white/30"
                        aria-label="Close image viewer"
                    >
                        <X className="w-5 h-5" />
                    </button>

                    <button
                        type="button"
                        onClick={showPrevAdditionalImage}
                        className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/20 text-white hover:bg-white/30"
                        aria-label="Previous image"
                    >
                        <ChevronLeft className="w-6 h-6" />
                    </button>

                    <img
                        src={additionalImageUrls[selectedAdditionalImageIndex]}
                        alt={`Additional image ${selectedAdditionalImageIndex + 1}`}
                        className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg"
                    />

                    <button
                        type="button"
                        onClick={showNextAdditionalImage}
                        className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/20 text-white hover:bg-white/30"
                        aria-label="Next image"
                    >
                        <ChevronRight className="w-6 h-6" />
                    </button>
                </div>
            )}
        </>
    );
};

export default EventDetailPage;
